import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { MediaFrame } from '@/components/generals/media-frame'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { dayAtMorrow } from '@/constants/pages'

interface DayTimelineProps {
    index: string
}

/** A day on the estate from first swim to last dinner, told in six moments. */
export const DayTimeline: FC<DayTimelineProps> = ({ index }): ReactNode => {
    return (
        <section id='a-day-at-morrow' aria-labelledby='day-heading' className='relative z-10 bg-paper text-charcoal'>
            <div className='px-5 py-24 sm:px-8 lg:px-12 lg:py-32'>
                <div className='max-w-3xl'>
                    <SectionLabel index={index} className='text-charcoal/70'>
                        A day at Morrow
                    </SectionLabel>

                    <SplitReveal
                        as='h2'
                        id='day-heading'
                        className='mt-6 text-[clamp(2.1rem,4.2vw,4.2rem)] font-light leading-[1.02] tracking-[-0.03em]'
                    >
                        From the first swim <em className='font-serif font-normal'>to the last light.</em>
                    </SplitReveal>
                </div>

                <ol className='mt-16 lg:mt-24'>
                    {dayAtMorrow.map((moment, momentIndex) => {
                        const flipped = momentIndex % 2 === 1

                        return (
                            <li
                                key={moment.time}
                                className='grid items-center gap-8 border-t border-charcoal/15 py-10 lg:grid-cols-12 lg:gap-8 lg:py-14'
                            >
                                <div data-reveal className={cn('lg:col-span-5', flipped && 'lg:order-2 lg:col-start-8')}>
                                    <p className='type-wide text-[clamp(3rem,6vw,5.5rem)] font-light leading-none tracking-[-0.04em] text-forest tabular-nums'>
                                        {moment.time}
                                    </p>

                                    <h3 className='type-wide mt-6 text-xl font-medium uppercase'>
                                        {moment.title}
                                    </h3>

                                    <p className='mt-3 max-w-sm text-[15px] leading-relaxed text-charcoal/70'>
                                        {moment.description}
                                    </p>
                                </div>

                                <MediaFrame
                                    image={moment.image}
                                    sizes='(min-width: 1024px) 50vw, 100vw'
                                    mask={flipped ? 'left' : 'right'}
                                    parallax={6}
                                    className={cn('aspect-[16/10] rounded-[4px] lg:col-span-6', flipped ? 'lg:order-1 lg:col-start-1' : 'lg:col-start-7')}
                                />
                            </li>
                        )
                    })}
                </ol>
            </div>
        </section>
    )
}
